import { CSSProperties, RefObject, useLayoutEffect, useRef, useState } from 'react'

interface DashboardReskinFitScale {
  containerRef: RefObject<HTMLDivElement>
  contentRef: RefObject<HTMLDivElement>
  style: CSSProperties
}

const FIT_EPSILON = 0.001

const getContentBoxSize = (element: HTMLElement): { height: number, width: number } => {
  const styles = window.getComputedStyle(element)
  const paddingX = (Number.parseFloat(styles.paddingLeft) || 0) + (Number.parseFloat(styles.paddingRight) || 0)
  const paddingY = (Number.parseFloat(styles.paddingTop) || 0) + (Number.parseFloat(styles.paddingBottom) || 0)
  return {
    height: element.clientHeight - paddingY,
    width: element.clientWidth - paddingX
  }
}

export const useDashboardReskinFitScale = (
  layoutKey: boolean = false
): DashboardReskinFitScale => {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const widthPercentRef = useRef(100)
  const [widthPercent, setWidthPercent] = useState(100)

  useLayoutEffect(() => {
    widthPercentRef.current = 100
    setWidthPercent(100)

    const container = containerRef.current
    const content = contentRef.current
    if (container == null || content == null || typeof window.ResizeObserver === 'undefined') {
      return
    }

    const updateScale = (): void => {
      const available = getContentBoxSize(container)
      const heightFit = content.scrollHeight === 0 ? 1 : available.height / content.scrollHeight
      const widthFit = content.scrollWidth === 0 ? 1 : available.width / content.scrollWidth
      const nextPercent = Math.min(100, widthPercentRef.current * Math.min(heightFit, widthFit))
      const safePercent = Number.isFinite(nextPercent) && nextPercent > 0 ? nextPercent : 100
      if (Math.abs(widthPercentRef.current - safePercent) < FIT_EPSILON) {
        return
      }
      widthPercentRef.current = safePercent
      setWidthPercent(safePercent)
    }

    const observer = new ResizeObserver(updateScale)
    observer.observe(container)
    observer.observe(content)
    updateScale()

    return () => {
      observer.disconnect()
    }
  }, [layoutKey])

  return {
    containerRef,
    contentRef,
    style: { width: `${widthPercent}%` }
  }
}
